import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import {
  Loader2, AlertCircle, Eye, Cpu, Layers, Clock, Sparkles,
  ArrowRight, Globe, Lock,
} from "lucide-react";
import ProjectVisualizer from "@/components/ProjectVisualizer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LANGS = [
  { code: "de", label: "DE", name: "Deutsch" },
  { code: "en", label: "EN", name: "English" },
  { code: "fr", label: "FR", name: "Français" },
  { code: "es", label: "ES", name: "Español" },
];

function ExecSummaryBlock({ markdown }) {
  if (!markdown) return null;
  // Parse the strict numbered-phase + bullet format produced by GPT.
  const lines = markdown.split(/\r?\n/);
  const blocks = [];
  let current = null;
  for (const raw of lines) {
    const line = raw.trimEnd();
    const phaseMatch = line.match(/^\s*(\d+)\.\s+(.*)$/);
    const bulletMatch = line.match(/^\s*[-•]\s+(.*)$/);
    if (phaseMatch) {
      if (current) blocks.push(current);
      current = { num: phaseMatch[1], title: phaseMatch[2], bullets: [] };
    } else if (bulletMatch && current) {
      current.bullets.push(bulletMatch[1]);
    }
  }
  if (current) blocks.push(current);
  if (!blocks.length) {
    return (
      <pre className="text-sm text-[#A0A0B0] whitespace-pre-wrap font-['JetBrains_Mono'] leading-relaxed">
        {markdown}
      </pre>
    );
  }
  return (
    <ol className="space-y-5">
      {blocks.map((b) => (
        <li key={b.num} className="flex gap-4">
          <span
            className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-xs font-['JetBrains_Mono'] font-bold border"
            style={{ background: "#00F0FF14", borderColor: "#00F0FF55", color: "#00F0FF" }}
          >
            {b.num}
          </span>
          <div className="flex-1 min-w-0">
            <h4 className="font-['Outfit'] text-base text-white font-semibold leading-tight mb-2">
              {b.title}
            </h4>
            <ul className="space-y-1.5">
              {b.bullets.map((bl, i) => (
                <li key={i} className="text-sm text-[#C8C8D2] leading-relaxed flex gap-2">
                  <span className="text-[#00F0FF]/60 mt-1">▸</span>
                  <span>{bl}</span>
                </li>
              ))}
            </ul>
          </div>
        </li>
      ))}
    </ol>
  );
}

export default function ShareView() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lang, setLang] = useState(() => localStorage.getItem("share_lang") || "de");
  const [translating, setTranslating] = useState(false);

  const load = useCallback(async (targetLang) => {
    try {
      const res = await axios.get(`${API}/share/${token}`, { params: { lang: targetLang } });
      setData(res.data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Teaser-Link nicht gefunden.");
    }
  }, [token]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      await load(lang);
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
    // initial load only — language switches handled via switchLang
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const switchLang = async (next) => {
    if (next === lang || translating) return;
    setLang(next);
    localStorage.setItem("share_lang", next);
    setTranslating(true);
    await load(next);
    setTranslating(false);
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-[#05050A] flex items-center justify-center">
        <Loader2 className="w-10 h-10 text-[#00F0FF] animate-spin" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="fixed inset-0 bg-[#05050A] flex items-center justify-center p-6">
        <div className="text-center max-w-sm">
          <Lock className="w-12 h-12 text-[#FF3B30] mx-auto mb-4" />
          <h2 className="font-['Outfit'] text-xl text-white mb-2">Teaser nicht verfügbar</h2>
          <p className="text-[#A0A0B0] text-sm">{error || "Der Link wurde widerrufen oder ist ungültig."}</p>
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 mt-5 text-sm text-[#00F0FF] hover:text-white transition-colors font-['Outfit']"
          >
            Zur Startseite <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    );
  }

  const a = data.analysis || {};
  const screens = (a.visualElements || []).filter((v) => v.type === "Plane").length;
  const funcs = (a.visualElements || []).filter((v) => v.type === "Cube").length;
  const hMin = a.estimatedHours_min || 0;
  const hMax = a.estimatedHours_max || 0;

  const i18n = {
    de: { teaser: "Teaser-Vorschau", complexity: "Komplexität", screens: "Frontends", funcs: "Backend", hours: "Stunden", techStack: "Empfohlener Tech-Stack", execSummary: "Executive Summary · Roadmap", milestones: "Meilensteine", cta: "Eigenes Projekt analysieren", lang_label: "Sprache der Zusammenfassung", roadmap_lang: "Diese Roadmap wird live in deine Sprache übersetzt.", view_3d: "3D-Architektur", footer_disclaimer: "Read-only Vorschau — Project Radar AR" },
    en: { teaser: "Teaser preview", complexity: "Complexity", screens: "Frontends", funcs: "Backend", hours: "Hours", techStack: "Recommended tech stack", execSummary: "Executive Summary · Roadmap", milestones: "Milestones", cta: "Analyse my own project", lang_label: "Summary language", roadmap_lang: "This roadmap is translated live into your language.", view_3d: "3D Architecture", footer_disclaimer: "Read-only preview — Project Radar AR" },
    fr: { teaser: "Aperçu Teaser", complexity: "Complexité", screens: "Frontends", funcs: "Backend", hours: "Heures", techStack: "Stack technique recommandé", execSummary: "Executive Summary · Feuille de route", milestones: "Jalons", cta: "Analyser mon projet", lang_label: "Langue du résumé", roadmap_lang: "Cette feuille de route est traduite en direct dans votre langue.", view_3d: "Architecture 3D", footer_disclaimer: "Aperçu en lecture seule — Project Radar AR" },
    es: { teaser: "Vista teaser", complexity: "Complejidad", screens: "Frontend", funcs: "Backend", hours: "Horas", techStack: "Stack tecnológico recomendado", execSummary: "Executive Summary · Hoja de ruta", milestones: "Hitos", cta: "Analizar mi proyecto", lang_label: "Idioma del resumen", roadmap_lang: "Esta hoja de ruta se traduce al instante a tu idioma.", view_3d: "Arquitectura 3D", footer_disclaimer: "Vista de solo lectura — Project Radar AR" },
  };
  const t = i18n[lang] || i18n.de;

  return (
    <div data-testid="share-view-page" className="min-h-screen bg-[#05050A] text-white">
      {/* Top header — minimal, chrome-less */}
      <header className="border-b border-white/5 bg-[#060610]/80 backdrop-blur-xl sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-md bg-[#00F0FF]/15 border border-[#00F0FF]/40 flex items-center justify-center">
              <Eye className="w-4 h-4 text-[#00F0FF]" />
            </div>
            <div>
              <p className="text-[10px] tracking-[0.25em] uppercase text-[#00F0FF] font-['JetBrains_Mono']">{t.teaser}</p>
              <p className="text-xs text-[#909098] font-['Outfit']">Project Radar AR</p>
            </div>
          </div>

          <div className="ml-auto flex items-center gap-2" data-testid="share-lang-switcher">
            <Globe className="w-3.5 h-3.5 text-[#909098]" />
            <span className="text-[11px] text-[#909098] font-['Outfit'] hidden sm:inline">{t.lang_label}:</span>
            <div className="flex items-center gap-1">
              {LANGS.map((l) => (
                <button
                  key={l.code}
                  data-testid={`share-lang-${l.code}`}
                  onClick={() => switchLang(l.code)}
                  disabled={translating}
                  title={l.name}
                  className={`px-2.5 py-1 rounded-md text-[11px] font-['JetBrains_Mono'] tracking-wider transition-all border ${
                    lang === l.code
                      ? "bg-[#00F0FF]/15 border-[#00F0FF]/50 text-[#00F0FF]"
                      : "bg-white/5 border-white/10 text-[#909098] hover:text-white hover:bg-white/10"
                  }`}
                >
                  {l.label}
                </button>
              ))}
              {translating && <Loader2 className="w-3.5 h-3.5 text-[#00F0FF] animate-spin ml-1" />}
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-10 space-y-8">
        {/* Hero */}
        <section>
          <span className="inline-block text-[10px] tracking-[0.25em] uppercase text-[#00F0FF] font-['JetBrains_Mono'] mb-2">
            {data.projectCategory}
          </span>
          <h1 className="font-['Outfit'] text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight mb-3">
            {data.projectTitle}
          </h1>
          <p className="text-[#A0A0B0] text-sm sm:text-base leading-relaxed max-w-3xl whitespace-pre-line">
            {data.projectDescription}
          </p>
        </section>

        {/* KPI strip */}
        <section className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div className="glass-surface rounded-xl p-4 border border-white/5">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-[#909098] font-['JetBrains_Mono'] mb-2">
              <Cpu className="w-3 h-3" /> {t.complexity}
            </div>
            <p className="text-xl font-['Outfit'] font-semibold text-white">{a.projectComplexity || "—"}</p>
          </div>
          <div className="glass-surface rounded-xl p-4 border border-white/5">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-[#909098] font-['JetBrains_Mono'] mb-2">
              <Layers className="w-3 h-3" /> {t.screens}
            </div>
            <p className="text-xl font-['Outfit'] font-semibold text-white">{screens}</p>
          </div>
          <div className="glass-surface rounded-xl p-4 border border-white/5">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-[#909098] font-['JetBrains_Mono'] mb-2">
              <Sparkles className="w-3 h-3" /> {t.funcs}
            </div>
            <p className="text-xl font-['Outfit'] font-semibold text-white">{funcs}</p>
          </div>
          <div className="glass-surface rounded-xl p-4 border border-white/5">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-[#909098] font-['JetBrains_Mono'] mb-2">
              <Clock className="w-3 h-3" /> {t.hours}
            </div>
            <p className="text-xl font-['Outfit'] font-semibold text-white">
              {hMin}<span className="text-[#909098] mx-1">–</span>{hMax}
            </p>
          </div>
        </section>

        {/* 3D preview */}
        {(a.visualElements || []).length > 0 && (
          <section data-testid="share-3d-section">
            <div className="flex items-center gap-2 mb-3">
              <span className="text-[10px] tracking-[0.25em] uppercase text-[#00F0FF] font-['JetBrains_Mono']">
                {t.view_3d}
              </span>
            </div>
            <ProjectVisualizer visualElements={a.visualElements} analysis={a} />
          </section>
        )}

        {/* Tech Stack */}
        {(a.techStack_reco || []).length > 0 && (
          <section>
            <h3 className="font-['Outfit'] text-base font-semibold text-white mb-3">{t.techStack}</h3>
            <div className="flex flex-wrap gap-2">
              {a.techStack_reco.map((tech, i) => (
                <span
                  key={i}
                  className="px-3 py-1.5 rounded-md bg-[#00F0FF]/10 border border-[#00F0FF]/30 text-[#00F0FF] text-xs font-['JetBrains_Mono']"
                >
                  {tech}
                </span>
              ))}
            </div>
          </section>
        )}

        {/* Executive Summary */}
        {a.executiveSummary && (
          <section data-testid="share-exec-summary" className="glass-surface rounded-2xl p-6 sm:p-8 border border-white/8">
            <div className="flex items-center justify-between gap-3 flex-wrap mb-5">
              <h2 className="font-['Outfit'] text-xl sm:text-2xl text-white font-light tracking-tight">
                {t.execSummary}
              </h2>
              <span className="text-[10px] text-[#909098] font-['JetBrains_Mono'] uppercase tracking-widest">
                {data.displayedLang?.toUpperCase()}
              </span>
            </div>
            <ExecSummaryBlock markdown={a.executiveSummary} />
            <p className="mt-5 pt-4 border-t border-white/5 text-[10px] text-[#606070] font-['JetBrains_Mono']">
              {t.roadmap_lang}
            </p>
          </section>
        )}

        {/* Milestones */}
        {(a.milestones || []).length > 0 && (
          <section>
            <h3 className="font-['Outfit'] text-base font-semibold text-white mb-3">{t.milestones}</h3>
            <ul className="space-y-2">
              {a.milestones.map((m, i) => (
                <li
                  key={i}
                  className="flex items-center gap-3 px-4 py-3 rounded-lg bg-white/[0.03] border border-white/8"
                >
                  <span className="w-7 h-7 rounded-full bg-[#00F0FF]/10 border border-[#00F0FF]/30 text-[#00F0FF] text-xs font-['JetBrains_Mono'] flex items-center justify-center flex-shrink-0">
                    {m.phase || i + 1}
                  </span>
                  <span className="flex-1 text-sm text-white font-['Outfit'] truncate">{m.name}</span>
                  <span className="text-[11px] text-[#909098] font-['JetBrains_Mono']">
                    {m.weeks ? `${m.weeks}w` : ""}
                  </span>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* CTA — viral hook */}
        <section className="rounded-2xl p-6 sm:p-8 text-center bg-gradient-to-br from-[#00F0FF]/[0.08] via-transparent to-[#FFB800]/[0.05] border border-[#00F0FF]/20">
          <p className="text-xs text-[#909098] font-['JetBrains_Mono'] tracking-widest uppercase mb-3">
            {t.footer_disclaimer}
          </p>
          <Link
            to="/"
            data-testid="share-cta-btn"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-[#00F0FF] text-black font-['Outfit'] font-semibold text-sm hover:bg-white transition-colors"
          >
            {t.cta} <ArrowRight className="w-4 h-4" />
          </Link>
        </section>
      </main>
    </div>
  );
}
